import { createDeleteOperation } from '~/services/api/createDeleteOperation'
import { buildMultipartBody } from '~/utils/buildMultipartBody'
import type { z } from 'zod'
import { requestGeneratedResource } from '~/services/api/requestGeneratedResource'
import type { MenuItemFormInput, MenuItemsQueryInput } from './interfaces'
import * as operations from '../../generated'
import { MenuItemStore } from '~/services/api/contracts/requests/MenuItemStore'
import { MenuItemUpdate } from '~/services/api/contracts/requests/MenuItemUpdate'
import { MenuItemUpdateStatus } from '~/services/api/contracts/requests/MenuItemUpdateStatus'
import { MenuItemIndex } from '~/services/api/contracts/requests/MenuItemIndex'
import { MenuItemOptions } from '~/services/api/contracts/requests/MenuItemOptions'
import { MenuItemSyncPlacements } from '~/services/api/contracts/requests/MenuItemSyncPlacements'

const canonicalNumber = (value: number | string): number => {
  if (typeof value === 'number') return value
  return value.trim() === '' ? Number.NaN : Number(value)
}

const canonicalNullableNumber = (value: number | string | null): number | null =>
  value === null ? null : canonicalNumber(value)

const canonicalQuery = (query: MenuItemsQueryInput) => ({
  ...(query.brand_id !== undefined ? { brand_id: query.brand_id } : {}),
  ...(query.category !== undefined
    ? { category: query.category === null ? null : String(query.category) }
    : {}),
  ...(query.category_ids !== undefined ? { 'category_ids[]': query.category_ids.map(String) } : {}),
  ...(query.name !== undefined ? { name: query.name } : {}),
  ...(query.page !== undefined ? { page: query.page } : {}),
  ...(query.per_page !== undefined ? { per_page: query.per_page } : {}),
  ...(query.sort_by !== undefined ? { sort_by: query.sort_by } : {}),
  ...(query.sort_direction !== undefined ? { sort_direction: query.sort_direction } : {}),
  ...(query.store_id !== undefined ? { store_id: query.store_id } : {})
})

const canonicalRequest = (payload: Partial<MenuItemFormInput>) => ({
  ...(payload.customization_group_ids !== undefined
    ? { customization_group_ids: payload.customization_group_ids.map(canonicalNumber) }
    : {}),
  ...(payload.customization_groups !== undefined
    ? {
        customization_groups: payload.customization_groups.map((group) => ({
          id: canonicalNumber(group.id),
          ...(group.sort_order !== undefined
            ? { sort_order: canonicalNumber(group.sort_order) }
            : {})
        }))
      }
    : {}),
  ...(payload.default_price !== undefined
    ? { default_price: canonicalNumber(payload.default_price) }
    : {}),
  ...(payload.description !== undefined ? { description: payload.description } : {}),
  ...(payload.dietary_attribute_ids !== undefined
    ? { dietary_attribute_ids: payload.dietary_attribute_ids.map(canonicalNumber) }
    : {}),
  ...(payload.individual_packaging_available !== undefined
    ? { individual_packaging_available: payload.individual_packaging_available }
    : {}),
  ...(payload.is_available !== undefined ? { is_available: payload.is_available } : {}),
  ...(payload.is_featured !== undefined ? { is_featured: payload.is_featured } : {}),
  ...(payload.is_package !== undefined ? { is_package: payload.is_package } : {}),
  ...(payload.menu_category_id !== undefined
    ? {
        menu_category_id:
          payload.menu_category_id === null ? null : canonicalNumber(payload.menu_category_id)
      }
    : {}),
  ...(payload.menu_id !== undefined ? { menu_id: canonicalNumber(payload.menu_id) } : {}),
  // A guest count has no null floor below 1 (APP-1545): a cleared field sends 1, never null.
  ...(payload.minimum_guest_count !== undefined
    ? {
        minimum_guest_count:
          payload.minimum_guest_count === null ? 1 : canonicalNumber(payload.minimum_guest_count)
      }
    : {}),
  ...(payload.name !== undefined ? { name: payload.name } : {}),
  ...(payload.out_of_stock !== undefined ? { out_of_stock: payload.out_of_stock } : {}),
  ...(payload.photo === undefined || payload.photo === null ? {} : { photo: payload.photo }),
  ...(payload.price_type !== undefined ? { price_type: payload.price_type } : {}),
  ...(payload.serves_count !== undefined ? { serves_count: payload.serves_count } : {}),
  ...(payload.sort_order !== undefined ? { sort_order: canonicalNumber(payload.sort_order) } : {}),
  ...(payload.status !== undefined ? { status: payload.status } : {}),
  ...(payload.tax_category !== undefined ? { tax_category: payload.tax_category } : {}),
  ...(payload.weight_unit !== undefined ? { weight_unit: payload.weight_unit } : {}),
  ...(payload.weight_value !== undefined
    ? { weight_value: canonicalNullableNumber(payload.weight_value) }
    : {})
})

type MenuItemStoreRequest = z.output<ReturnType<typeof MenuItemStore>>
type MenuItemMultipartRequest = {
  [Field in keyof MenuItemStoreRequest]?: MenuItemStoreRequest[Field] | undefined
}

// A list that is empty sends no part at all; buildMultipartBody would send a lone empty `key[]`.
const nonEmptyList = (items: readonly number[] | undefined): readonly number[] | undefined =>
  items === undefined || items.length === 0 ? undefined : items

const customizationGroupFields = (
  groups: MenuItemMultipartRequest['customization_groups']
): Record<string, number | undefined> => {
  const fields: Record<string, number | undefined> = {}
  for (const [index, group] of (groups ?? []).entries()) {
    fields[`customization_groups[${index}][id]`] = group.id
    fields[`customization_groups[${index}][sort_order]`] = group.sort_order
  }
  return fields
}

const menuItemMultipartFields = (request: MenuItemMultipartRequest) => ({
  customization_group_ids: nonEmptyList(request.customization_group_ids),
  ...customizationGroupFields(request.customization_groups),
  default_price: request.default_price,
  description: request.description,
  dietary_attribute_ids: nonEmptyList(request.dietary_attribute_ids),
  individual_packaging_available: request.individual_packaging_available,
  is_available: request.is_available,
  is_featured: request.is_featured,
  is_package: request.is_package,
  menu_category_id: request.menu_category_id,
  menu_id: request.menu_id,
  minimum_guest_count: request.minimum_guest_count,
  name: request.name,
  out_of_stock: request.out_of_stock,
  photo: request.photo,
  price_type: request.price_type,
  serves_count: request.serves_count,
  sort_order: request.sort_order,
  status: request.status,
  tax_category: request.tax_category,
  weight_unit: request.weight_unit,
  weight_value: request.weight_value
})

export function useMenusItemsService() {
  const apiBaseUrl = useRuntimeConfig().public.apiBaseUrl
  if (typeof apiBaseUrl !== 'string' || apiBaseUrl.length === 0) {
    throw new TypeError('Runtime configuration requires public.apiBaseUrl.')
  }
  const baseURL = `${apiBaseUrl}/api/dashboard`
  const sanctumClient = useApiRequest()
  const deleteMenuItem = createDeleteOperation(
    sanctumClient,
    (menuId, itemId) => operations.MenuItemDestroyPathSchema.strict().parse({ menuId, itemId }),
    operations.MenuItemDestroySuccessSchema,
    ({ menuId, itemId }) => `${baseURL}/menus/${menuId}/items/${itemId}`
  )

  async function getMenuItemsOptions(
    menuId: operations.MenuItemOptionsPath['menuId'],
    query: MenuItemsQueryInput = {}
  ): Promise<operations.MenuItemOptionsSuccess['data']> {
    const path = operations.MenuItemOptionsPathSchema.strict().parse({ menuId })
    const request = MenuItemOptions({}).parse(canonicalQuery(query))
    const response = operations.MenuItemOptionsSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/items/options`, { query: request })
    )
    return structuredClone(response.data)
  }

  async function getMenuItemsByMenuId(
    menuId: operations.MenuItemIndexPath['menuId'],
    query: MenuItemsQueryInput = {}
  ): Promise<{
    data: operations.MenuItemManagementDto[]
    meta: operations.MenuItemIndexSuccess['data']['pagination']
  }> {
    const path = operations.MenuItemIndexPathSchema.strict().parse({ menuId })
    const request = MenuItemIndex({}).parse(canonicalQuery(query))
    const response = operations.MenuItemIndexSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/items`, {
        globalErrorUi: true,
        query: request
      })
    )
    return structuredClone({
      data: response.data.items,
      meta: response.data.pagination
    })
  }

  async function createMenuItem(
    menuId: operations.MenuItemStorePath['menuId'],
    payload: MenuItemFormInput
  ): Promise<operations.MenuItemManagementDto> {
    const path = operations.MenuItemStorePathSchema.strict().parse({ menuId })
    const request = MenuItemStore({ menu_id_required: false }).parse(canonicalRequest(payload))
    const formData = buildMultipartBody(menuItemMultipartFields(request))
    const response = operations.MenuItemStoreSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/items`, {
        body: formData,
        method: 'POST'
      })
    )
    return structuredClone(response.data)
  }

  async function updateMenuItem(
    menuId: operations.MenuItemUpdatePath['menuId'],
    itemId: operations.MenuItemUpdatePath['itemId'],
    payload: Partial<MenuItemFormInput>
  ): Promise<operations.MenuItemManagementDto> {
    const path = operations.MenuItemUpdatePathSchema.strict().parse({
      itemId,
      menuId
    })
    const request = MenuItemUpdate({}).parse(canonicalRequest(payload))
    const formData = buildMultipartBody(menuItemMultipartFields(request))
    const response = operations.MenuItemUpdateSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/items/${path.itemId}`, {
        body: formData,
        method: 'PUT'
      })
    )
    return structuredClone(response.data)
  }

  async function updateMenuItemStatus(
    menuId: operations.MenuItemUpdateStatusPath['menuId'],
    itemId: operations.MenuItemUpdateStatusPath['itemId'],
    payload: operations.MenuItemUpdateStatusRequest
  ): Promise<operations.MenuItemUpdateStatusSuccess['data']> {
    const request = MenuItemUpdateStatus({}).parse(payload)
    const response = await requestGeneratedResource(
      sanctumClient,
      {
        schema: operations.MenuItemUpdateStatusPathSchema,
        value: { itemId, menuId },
        buildUrl: (path) => `${baseURL}/menus/${path.menuId}/items/${path.itemId}/status`
      },
      operations.MenuItemUpdateStatusSuccessSchema,
      'PUT',
      { channel: 'body', value: request }
    )
    return structuredClone(response.data)
  }

  async function assignMenuItem(
    menuId: operations.MenuItemAssignToMenuPath['menuId'],
    itemId: operations.MenuItemAssignToMenuPath['itemId']
  ): Promise<operations.MenuItemAssignToMenuSuccess['data']> {
    const path = operations.MenuItemAssignToMenuPathSchema.strict().parse({ itemId, menuId })
    const response = operations.MenuItemAssignToMenuSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/items/${path.itemId}/assign`, {
        method: 'POST'
      })
    )
    return structuredClone(response.data)
  }

  async function syncMenuItemPlacements(
    menuId: operations.MenuItemSyncPlacementsPath['menuId'],
    itemId: operations.MenuItemSyncPlacementsPath['itemId'],
    placements: operations.MenuItemSyncPlacementsRequest['placements']
  ): Promise<operations.MenuItemSyncPlacementsSuccess['data']> {
    const path = operations.MenuItemSyncPlacementsPathSchema.strict().parse({ itemId, menuId })
    const request = MenuItemSyncPlacements({}).parse({ placements })
    const response = operations.MenuItemSyncPlacementsSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/items/${path.itemId}/placements`, {
        body: request,
        method: 'PUT'
      })
    )
    return structuredClone(response.data)
  }

  async function unassignMenuItem(
    menuId: operations.MenuItemUnassignFromMenuPath['menuId'],
    itemId: operations.MenuItemUnassignFromMenuPath['itemId']
  ): Promise<void> {
    const path = operations.MenuItemUnassignFromMenuPathSchema.strict().parse({ itemId, menuId })
    operations.MenuItemUnassignFromMenuSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/items/${path.itemId}/unassign`, {
        method: 'DELETE'
      })
    )
  }

  return {
    assignMenuItem,
    createMenuItem,
    deleteMenuItem,
    getMenuItemsByMenuId,
    getMenuItemsOptions,
    syncMenuItemPlacements,
    unassignMenuItem,
    updateMenuItem,
    updateMenuItemStatus
  }
}
